import { useEffect, useRef, useState } from 'react'
import type { BlockComment, DocBlock, DocumentPresence } from '@hub/contracts'
import { DocumentClient, type DocumentStatus } from './document-client'

export interface UseDocumentClientResult {
  client: DocumentClient | null
  /** First snapshot (cache or server) has arrived — the editor can mount. */
  ready: boolean
  /** Server snapshot applied (the cache may have rendered before this). */
  synced: boolean
  status: DocumentStatus
  initialBlocks: DocBlock[]
  presence: DocumentPresence[]
  comments: BlockComment[]
}

/** One DocumentClient per mounted document page. */
export function useDocumentClient(
  projectId: string,
  documentId: string,
  enabled = true
): UseDocumentClientResult {
  const [client, setClient] = useState<DocumentClient | null>(null)
  const [ready, setReady] = useState(false)
  const [synced, setSynced] = useState(false)
  const [status, setStatus] = useState<DocumentStatus>('connecting')
  const [presence, setPresence] = useState<DocumentPresence[]>([])
  const [comments, setComments] = useState<BlockComment[]>([])
  const initialBlocksRef = useRef<DocBlock[]>([])

  useEffect(() => {
    if (!enabled) return
    const c = new DocumentClient(projectId, documentId)
    initialBlocksRef.current = []
    setReady(false)
    setSynced(false)
    setPresence([])
    setComments([])
    const offs = [
      c.on('snapshot', (blocks, fromCache) => {
        if (initialBlocksRef.current.length === 0 || !fromCache) initialBlocksRef.current = blocks
        setReady(true)
        if (!fromCache) setSynced(true)
      }),
      c.on('status', setStatus),
      c.on('presence', setPresence),
      c.on('comments', setComments),
    ]
    setClient(c)
    void c.connect()
    return () => {
      for (const off of offs) off()
      c.close()
      setClient(null)
    }
  }, [projectId, documentId, enabled])

  return {
    client,
    ready,
    synced,
    status,
    initialBlocks: initialBlocksRef.current,
    presence,
    comments,
  }
}
